import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { LoadingPage } from '../../components/ui/LoadingSpinner';
import { useAuth } from '../../context/AuthContext';
import {
  Typography,
  Box,
  Card,
  CardContent,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  CalendarMonth as MonthlyIcon,
  CalendarToday as AnnualIcon,
  Subscriptions as SubscriptionsIcon,
  Warning as WarningIcon,
  CreditCard as CreditCardIcon,
} from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import EarningsOverview from '../../components/EarningsOverview';
import StripeAccountOnboarding from '../../components/StripeAccountOnboarding';
import apiClient from '../../lib/api';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading, user, currentAccountType } = useAuth();
  
  const [earnings, setEarnings] = useState(null);
  const [branches, setBranches] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [stripeConnectAccount, setStripeConnectAccount] = useState(null);
  const [isStripeConnectValid, setIsStripeConnectValid] = useState(true);
  const [showStripeOnboardingModal, setShowStripeOnboardingModal] = useState(false);
  const [onboardingAccountId, setOnboardingAccountId] = useState(null);
  const [showReportIssueDialog, setShowReportIssueDialog] = useState(false);
  const [issueDescription, setIssueDescription] = useState('');
  const [isSubmittingIssue, setIsSubmittingIssue] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
      return;
    }
    loadDashboardData();
  }, [isAuthenticated, authLoading, navigate]);

  const loadDashboardData = async () => {
    try {
      // Get company-specific earnings if user has a company
      let earnings;
      if (user?.companyId) {
        // For company users, calculate earnings from their branches/subscriptions
        const branchesResponse = await apiClient.getBranches({ companyId: user.companyId });
        const companyBranches = branchesResponse.data || [];
        
        // Calculate earnings based on company's business operations
        // This should show revenue from funeral services, not subscription costs
        // For now, we'll show placeholder data since we need to implement actual revenue tracking
        
        // TODO: Implement actual revenue calculation from:
        // - Funeral service sales
        // - Obituary sales
        // - Card sales
        // - Other company services
        
        earnings = {
          totalEarned: 0, // Placeholder - needs actual revenue data
          thisMonth: 0,   // Placeholder - needs actual monthly revenue
          pendingPayouts: 0, // Placeholder - needs actual pending revenue
          lastPayout: 0   // Placeholder - needs actual payout history
        };
      } else {
        // For admin users, get platform earnings from Stripe
        try {
          const earningsResponse = await apiClient.getPlatformEarnings();
          earnings = earningsResponse.data || {
            totalEarned: 0,
            thisMonth: 0,
            pendingPayouts: 0,
            lastPayout: 0
          };
        } catch (error) {
          console.error('Error loading platform earnings:', error);
          // Fallback to placeholder data if API fails
          earnings = {
            totalEarned: 0,
            thisMonth: 0,
            pendingPayouts: 0,
            lastPayout: 0
          };
        }
      }
      setEarnings(earnings);

      // Load branches for subscription stats
      const params = {};
      if (user?.companyId) {
        params.companyId = user.companyId;
      }
      
      const branchesResponse = await apiClient.getBranches(params);
      setBranches(branchesResponse.data || []);
      
      // Check Stripe Connect account validity (skip for staff users)
      if (currentAccountType !== 'staff') {
        try {
          const stripeConnectResponse = await apiClient.getMyStripeConnectAccount();
          setStripeConnectAccount(stripeConnectResponse.data);
          
          // Check if Stripe Connect account is valid using comprehensive verification check
          // Account is verified if: details_submitted, charges_enabled, payouts_enabled are all true,
          // and there are no past_due requirements
          const accountData = stripeConnectResponse.data;
          const isValid = stripeConnectResponse.success && 
            stripeConnectResponse.hasAccountId && 
            accountData && 
            accountData.details_submitted === true &&
            accountData.charges_enabled === true &&
            accountData.payouts_enabled === true &&
            (!accountData.requirements?.past_due || accountData.requirements.past_due.length === 0);
          
          setIsStripeConnectValid(isValid);
          console.log('Stripe Connect validation:', { 
            success: stripeConnectResponse.success,
            hasAccountId: stripeConnectResponse.hasAccountId,
            details_submitted: stripeConnectResponse.data?.details_submitted,
            charges_enabled: stripeConnectResponse.data?.charges_enabled,
            payouts_enabled: stripeConnectResponse.data?.payouts_enabled,
            isValid 
          });
        } catch (error) {
          console.error('Error checking Stripe Connect account:', error);
          setIsStripeConnectValid(false);
        }
      }
      
      setIsLoading(false);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setIsLoading(false);
    }
  };

  const handleCompleteSetup = () => {
    if (stripeConnectAccount?.id) {
      setOnboardingAccountId(stripeConnectAccount.id);
    } else {
      setOnboardingAccountId(null); // Will trigger account creation
    }
    setShowStripeOnboardingModal(true);
  };

  const handleStripeOnboardingComplete = () => {
    setShowStripeOnboardingModal(false);
    setOnboardingAccountId(null);
    // Reload the dashboard data to refresh Stripe Connect status
    loadDashboardData();
  };

  const handleStripeOnboardingError = (error) => {
    console.error('Stripe onboarding error:', error);
    // Keep modal open so user can retry
  };

  const handleOpenReportIssue = () => {
    setIssueDescription('');
    setShowReportIssueDialog(true);
  };

  const handleCloseReportIssue = () => {
    if (!isSubmittingIssue) {
      setShowReportIssueDialog(false);
      setIssueDescription('');
    }
  };

  const handleSubmitIssue = async () => {
    const trimmed = (issueDescription || '').trim();
    if (!trimmed) {
      toast.error('Please describe the issue.');
      return;
    }
    setIsSubmittingIssue(true);
    try {
      const result = await apiClient.submitIssueReport(trimmed);
      toast.success(result.message || 'Thank you. We’ll reach out if we need more details.');
      setShowReportIssueDialog(false);
      setIssueDescription('');
    } catch (err) {
      const message = err.response?.data?.error || 'Failed to submit. Please try again.';
      toast.error(message);
    } finally {
      setIsSubmittingIssue(false);
    }
  };

  if (authLoading) {
    return <LoadingPage />;
  }

  if (!isAuthenticated) {
    return null; // Will redirect to login
  }

  return (
    <DashboardLayout title="DASHBOARD">
      <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 2 }}>
        Dashboard Overview
      </Typography>

      {/* BETA Notice */}
      <Alert
        severity="info"
        sx={{
          mb: 3,
          backgroundColor: 'rgba(44, 69, 117, 0.08)',
          border: '1px solid rgba(44, 69, 117, 0.25)',
          '& .MuiAlert-message': { width: '100%' },
        }}
      >
        <Typography component="span" sx={{ fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.5px', color: '#2c4575', textTransform: 'uppercase', display: 'inline-block', mb: 0.5 }}>
          Beta
        </Typography>
        <Typography variant="body2" sx={{ color: '#2c4575', mb: 1.5 }}>
          IMObituary is currently in beta. We're actively improving the product and appreciate your feedback as an early adopter.
        </Typography>
        <Button
          onClick={handleOpenReportIssue}
          size="small"
          variant="outlined"
          sx={{
            borderColor: '#2c4575',
            color: '#2c4575',
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': {
              borderColor: '#1e3154',
              backgroundColor: 'rgba(44, 69, 117, 0.08)',
            },
          }}
        >
          Report an issue
        </Button>
      </Alert>

      {/* Report an issue dialog */}
      <Dialog open={showReportIssueDialog} onClose={handleCloseReportIssue} maxWidth="sm" fullWidth>
        <DialogTitle>Report an issue</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Describe what went wrong or what we could improve. We’ll save this with your account and may follow up with you.
          </Typography>
          <TextField
            autoFocus
            multiline
            minRows={4}
            maxRows={10}
            fullWidth
            placeholder="What happened? What were you trying to do?"
            value={issueDescription}
            onChange={(e) => setIssueDescription(e.target.value)}
            disabled={isSubmittingIssue}
            variant="outlined"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseReportIssue} disabled={isSubmittingIssue}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitIssue}
            variant="contained"
            disabled={isSubmittingIssue || !(issueDescription || '').trim()}
            sx={{
              backgroundColor: '#2c4575',
              '&:hover': { backgroundColor: '#1e3154' },
            }}
          >
            {isSubmittingIssue ? 'Submitting…' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Stripe Connect Warning - Not shown for admins or staff */}
      {!isStripeConnectValid && !isLoading && currentAccountType !== 'admin' && currentAccountType !== 'staff' && (
        <Alert 
          severity="info" 
          icon={<WarningIcon />}
          sx={{ mb: 3 }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Stripe Connect Account Setup
          </Typography>
          {/* DISABLED FOR TESTING - Restore when testing is complete
          <Typography variant="body2">
            We are currently in testing mode. Stripe Connect account setup will be available once testing is completed. 
            You will be able to set up your Stripe account to receive payments at that time.
          </Typography>
          */}
          <Typography variant="body2" sx={{ mb: 2 }}>
            Your Stripe Connect account needs to be completed before you can receive payments. 
            Please complete your account setup to enable payment processing.
          </Typography>
          <Button 
            variant="contained"
            size="medium"
            onClick={handleCompleteSetup}
            sx={{
              backgroundColor: '#2c4575',
              color: 'white',
              fontWeight: 600,
              textTransform: 'none',
              px: 3,
              py: 1,
              '&:hover': {
                backgroundColor: '#1e3154',
                transform: 'translateY(-1px)',
                boxShadow: 2
              },
              transition: 'all 0.2s ease-in-out'
            }}
          >
            Complete Setup
          </Button>
        </Alert>
      )}

      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <Typography>Loading dashboard data...</Typography>
        </div>
      ) : (
        <>
          <EarningsOverview 
            earnings={earnings}
            title={user?.companyId ? "Company Earnings Overview" : "Platform Earnings Overview"}
            hasStripeConnectAccount={!!stripeConnectAccount}
            isStripeConnectValid={isStripeConnectValid}
          />

          {/* Subscription Overview - Only visible when viewing as admin */}
          {currentAccountType === 'admin' && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" component="h2" gutterBottom>
                Subscription Overview
              </Typography>
              
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' },
                gap: 2
              }}>
                <Card sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 }
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <SubscriptionsIcon sx={{ mr: 1, fontSize: 24 }} />
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Total Subscriptions
                      </Typography>
                    </Box>
                    <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                      {branches.filter(b => b.subscriptionId || b.subscriptionPlan).length}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      Active branches
                    </Typography>
                  </CardContent>
                </Card>
                
                <Card sx={{
                  background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                  color: 'white',
                  '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 }
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <MonthlyIcon sx={{ mr: 1, fontSize: 24 }} />
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Monthly Plans
                      </Typography>
                    </Box>
                    <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                      {branches.filter(b => b.subscriptionPlan === 'monthly').length}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      ${branches.filter(b => b.subscriptionPlan === 'monthly').length * 50}/month
                    </Typography>
                  </CardContent>
                </Card>
                
                <Card sx={{
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  color: 'white',
                  '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 }
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <AnnualIcon sx={{ mr: 1, fontSize: 24 }} />
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Annual Plans
                      </Typography>
                    </Box>
                    <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                      {branches.filter(b => b.subscriptionPlan === 'annual').length}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      ${branches.filter(b => b.subscriptionPlan === 'annual').length * 350}/year
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            </Box>
          )}
        </>
      )}      

      {/* Stripe Connect Onboarding Modal */}
      <Dialog 
        open={showStripeOnboardingModal} 
        onClose={() => setShowStripeOnboardingModal(false)}
        maxWidth="lg"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 2,
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CreditCardIcon />
            <Typography variant="h6">Complete Your Stripe Account Setup</Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: 0, overflow: 'auto', flex: 1 }}>
          {onboardingAccountId && (
            <StripeAccountOnboarding
              accountId={onboardingAccountId}
              onComplete={handleStripeOnboardingComplete}
              onError={handleStripeOnboardingError}
            />
          )}
        </DialogContent>
        
        <DialogActions sx={{ px: 4, pb: 4, flexShrink: 0 }}>
          <Button
            variant="outlined"
            onClick={() => setShowStripeOnboardingModal(false)}
            sx={{ mr: 2 }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

    </DashboardLayout>
  );
};

export default AdminDashboard;
